<template>
  <section
    v-if="fields && sections.length"
    :class="rootClass"
    :style="rootStyle"
    :id="rendering?.dataSource"
  >
    <ContentGrid
      :desktop-default-grid="[2, 20]"
      :tablet-default-grid="[1, 14]"
      :mobile-default-grid="[1, 14]"
    >
      <div class="c-service-section__inner">
        <div class="c-service-section__sections">
          <article
            v-for="(section, sectionIndex) in sections"
            :key="section.id || section.fields.title?.value"
            class="c-service-section__group"
            :class="`c-service-section__group--${getTheme(sectionIndex)}`"
          >
            <header class="c-service-section__group-header">
              <JssRichText :field="section.fields?.title" tag="h2" />
            </header>

            <div class="c-service-section__group-body">
              <div class="c-service-section__venn">
                <span
                  class="c-service-section__venn-circle c-service-section__venn-circle--left"
                  aria-hidden="true"
                ></span>
                <span
                  class="c-service-section__venn-circle c-service-section__venn-circle--right"
                  aria-hidden="true"
                ></span>
                <span
                  class="c-service-section__venn-circle c-service-section__venn-circle--bottom"
                  aria-hidden="true"
                ></span>
                <Text
                  v-for="(item, index) in getVennItems(section)"
                  :key="item.id || item.fields.label?.value"
                  tag="span"
                  class="c-service-section__venn-label"
                  :class="`c-service-section__venn-label--${getPosition(index)}`"
                  :field="item.fields.label"
                />
              </div>

              <div class="c-service-section__services">
                <Text
                  v-if="section.fields.servicesTitle?.value"
                  tag="h3"
                  class="c-service-section__services-title"
                  :field="section.fields.servicesTitle"
                />

                <div class="c-service-section__cards">
                  <article
                    v-for="card in getServiceCards(section)"
                    :key="card.id || card.fields.title?.value"
                    class="c-service-section__card"
                  >
                    <div class="c-service-section__card-line">
                      <Text
                        v-if="card.fields.title?.value"
                        tag="strong"
                        class="c-service-section__card-title"
                        :field="card.fields.title"
                      />
                      <RichText
                        v-if="card.fields.content?.value"
                        tag="div"
                        class="c-service-section__card-content"
                        :field="card.fields.content"
                      />
                    </div>
                  </article>
                </div>
              </div>
            </div>
          </article>
        </div>

        <RichText
          v-if="fields.bottomStatement?.value"
          class="c-service-section__statement"
          :field="fields.bottomStatement"
        />
      </div>
    </ContentGrid>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RichText, Text } from '@sitecore-jss/sitecore-jss-vue';
import type {
  ServiceSectionCardFields,
  ServiceSectionGroupFields,
  ServiceSectionProps,
  ServiceSectionVennItemFields,
} from '@/types/components/ServiceSection';
import type { ScItem } from '@/types/sitecore';
import utils from '@/utils';

type ServiceSectionGroup = ScItem<ServiceSectionGroupFields> & {
  fields: ServiceSectionGroupFields;
};
type ServiceSectionVennItem = ScItem<ServiceSectionVennItemFields> & {
  fields: ServiceSectionVennItemFields;
};
type ServiceSectionCard = ScItem<ServiceSectionCardFields> & {
  fields: ServiceSectionCardFields;
};

const props = defineProps<ServiceSectionProps>();
const fields = computed(() => props.fields);
const rendering = computed(() => props.rendering);

const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'c-service-section'));
const rootStyle = computed(() => utils.overall?.baseComStyle?.(fields.value) || {});

const sections = computed<ServiceSectionGroup[]>(() => {
  const items = utils.sc.getFieldObj(fields.value, 'sections');
  return Array.isArray(items)
    ? items.filter((item): item is ServiceSectionGroup => Boolean(item?.fields))
    : [];
});

const getTheme = (index: number) => (index === 0 ? 'developability' : 'functionality');

const getPosition = (index: number) => ['left', 'right', 'bottom', 'center'][index] || 'center';

const getVennItems = (section: ServiceSectionGroup): ServiceSectionVennItem[] =>
  Array.isArray(section.fields.vennItems)
    ? section.fields.vennItems.filter((item): item is ServiceSectionVennItem =>
        Boolean(item?.fields)
      )
    : [];

const getServiceCards = (section: ServiceSectionGroup): ServiceSectionCard[] =>
  Array.isArray(section.fields.serviceCards)
    ? section.fields.serviceCards.filter((item): item is ServiceSectionCard =>
        Boolean(item?.fields)
      )
    : [];
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-service-section {
  padding: 40px 0;
  background: $white;
  color: #3f3f3f;

  &__inner {
    max-width: 1440px;
    margin: 0 auto;
  }

  &__sections {
    display: grid;
    grid-template-columns: 1fr;
    gap: 32px;
  }

  &__group-header {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 42px;
    border-radius: 7px;
    background: rgba(219, 201, 172, 1);
    color: $white;
    text-align: center;
  }

  &__group--functionality &__group-header {
    background: rgba(143, 166, 144, 1);
  }

  &__group-title {
    margin: 0;
    color: inherit;
    font-size: 18px;
    line-height: 1.2;
    font-weight: 700;
    letter-spacing: 0;
  }

  &__group-body {
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;
    margin-top: 26px;
  }

  &__venn {
    position: relative;
    width: min(100%, 292px);
    aspect-ratio: 1.09 / 1;
    margin: 0 auto;
  }

  &__venn-circle {
    position: absolute;
    width: 61%;
    aspect-ratio: 1;
    border-radius: 50%;
    background: rgba(223, 213, 191, 0.3);

    &--left {
      left: 0;
      top: 0;
    }

    &--right {
      right: 0;
      top: 0;
    }

    &--bottom {
      left: 19.5%;
      top: 31%;
    }
  }

  &__venn-label {
    position: absolute;
    z-index: 3;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30%;
    min-height: 42px;
    color: rgba(62, 62, 62, 1);
    text-align: center;
    font-size: 13px;
    line-height: 1.18;
    font-weight: 400;
    letter-spacing: 0;
    pointer-events: none;

    &--left {
      left: 8.5%;
      top: 17%;
    }

    &--right {
      right: 8.5%;
      top: 17%;
    }

    &--bottom {
      left: 31.5%;
      top: 68%;
      width: 37%;
    }

    &--center {
      left: 36.5%;
      top: 36%;
      width: 26%;
      min-height: 34px;
      font-size: 12px;
    }
  }

  &__group--functionality &__venn-circle {
    background: rgba(199, 211, 199, 0.3);
  }

  &__group--functionality &__venn-label--left {
    left: 8%;
    top: 17%;
  }

  &__group--functionality &__venn-label--right {
    right: 8%;
    top: 17%;
  }

  &__group--functionality &__venn-label--bottom {
    left: 28%;
    top: 68%;
    width: 44%;
  }

  &__group--functionality &__venn-label--center {
    top: 36%;
  }

  &__services-title {
    margin: 0 0 12px;
    color: #3e3e3e;
    font-size: 17px;
    line-height: 1.2;
    font-weight: 700;
  }

  &__cards {
    display: grid;
    gap: 10px;
  }

  &__card {
    border-radius: 8px;
    background: #f9f8f4;
    padding: 16px 18px;
    color: #4a4a4a;
    font-size: 15px;
    line-height: 1.32;
  }

  &__group--functionality &__card {
    background: #f0f4f0;
  }

  &__card-line {
    margin: 0;
  }

  &__card-title {
    display: inline;
    color: #3f3f3f;
    font-weight: 700;
  }

  &__card-content {
    display: inline;

    p {
      display: inline;
      margin: 0;
    }
    ul,
    ol {
      display: block;
      margin: 12px 0 0;
      padding-left: 18px;
    }

    li + li {
      margin-top: 5px;
    }

    p + ul,
    p + ol {
      margin-top: 12px;
    }
  }

  &__card-title + &__card-content {
    margin-left: 4px;
  }

  &__statement {
    margin-top: 34px;
    border-radius: 14px;
    background: rgba(239, 234, 223, 1);
    padding: 18px 22px;
    color: #383838;
    text-align: center;
    font-size: 15px;
    line-height: 1.45;
    font-weight: 700;

    p {
      margin: 0;
    }
  }

  @include tablet {
    padding: 56px 0;

    &__sections {
      gap: 40px;
    }

    &__group-title {
      font-size: 22px;
    }

    &__group-body {
      grid-template-columns: minmax(250px, 0.92fr) minmax(300px, 1.08fr);
      align-items: start;
      gap: 34px;
      margin-top: 32px;
    }

    &__venn {
      width: min(100%, 316px);
    }

    &__venn-label {
      font-size: 16px;
    }

    &__venn-label--center {
      font-size: 14px;
    }

    &__services-title {
      font-size: 20px;
    }

    &__card {
      padding: 18px 22px;
      font-size: 17px;
    }

    &__statement {
      padding: 21px 28px;
      font-size: 18px;
    }
  }

  @include tablet-landscape {
    padding: 64px 0;

    &__sections {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 46px;
    }

    &__group-body {
      grid-template-columns: minmax(245px, 0.92fr) minmax(320px, 1.08fr);
      gap: 36px;
    }

    &__venn {
      width: min(100%, 318px);
    }

    &__statement {
      margin-top: 42px;
    }
  }
}
</style>
